import React from 'react'
import { cn } from './cn'

// Tailwind's purge step (postcss7-compat / PurgeCSS) only keeps class names
// it can find as literal text in the source, so a dynamically built string
// like `tw-grid-cols-${n}` would get stripped in production builds. These
// lookup tables trade genericity for purge-safety: every class Row/Col can
// ever render already appears literally below. Add more counts/breakpoints
// here as later migrations need them.
const COLS = {
  base: {
    1: 'tw-grid-cols-1',
    2: 'tw-grid-cols-2',
    3: 'tw-grid-cols-3',
    4: 'tw-grid-cols-4',
    6: 'tw-grid-cols-6',
    12: 'tw-grid-cols-12',
  },
  sm: {
    1: 'sm:tw-grid-cols-1',
    2: 'sm:tw-grid-cols-2',
    3: 'sm:tw-grid-cols-3',
    4: 'sm:tw-grid-cols-4',
    6: 'sm:tw-grid-cols-6',
    12: 'sm:tw-grid-cols-12',
  },
  md: {
    1: 'md:tw-grid-cols-1',
    2: 'md:tw-grid-cols-2',
    3: 'md:tw-grid-cols-3',
    4: 'md:tw-grid-cols-4',
    6: 'md:tw-grid-cols-6',
    12: 'md:tw-grid-cols-12',
  },
  lg: {
    1: 'lg:tw-grid-cols-1',
    2: 'lg:tw-grid-cols-2',
    3: 'lg:tw-grid-cols-3',
    4: 'lg:tw-grid-cols-4',
    6: 'lg:tw-grid-cols-6',
    12: 'lg:tw-grid-cols-12',
  },
}

// Column *spans*, for the (rarer) case of a genuinely asymmetric row — e.g.
// a 9/3 split — that Row's equal-width `cols` count can't express. Pass
// `span` to individual `<Col>`s AND `cols={{ base: 12 }}` to the parent
// `<Row>` (span-N only means something inside a >=N column grid track).
const SPAN = {
  base: {
    3: 'tw-col-span-3',
    4: 'tw-col-span-4',
    6: 'tw-col-span-6',
    8: 'tw-col-span-8',
    9: 'tw-col-span-9',
    12: 'tw-col-span-12',
  },
  md: {
    3: 'md:tw-col-span-3',
    4: 'md:tw-col-span-4',
    6: 'md:tw-col-span-6',
    8: 'md:tw-col-span-8',
    9: 'md:tw-col-span-9',
    12: 'md:tw-col-span-12',
  },
  lg: {
    3: 'lg:tw-col-span-3',
    4: 'lg:tw-col-span-4',
    6: 'lg:tw-col-span-6',
    8: 'lg:tw-col-span-8',
    9: 'lg:tw-col-span-9',
    12: 'lg:tw-col-span-12',
  },
}

/**
 * Minimal Tailwind replacement for CoreUI's `<CRow>`. Takes a `cols` map of
 * { base, sm, md, lg } -> column count instead of CoreUI's per-`<CCol>` size
 * props — the columns it contains are expected to be equal-width, which
 * covers most uses of `<CRow>`/`<CCol>` in this codebase. For the rarer
 * asymmetric row, pass `cols={{ base: 12 }}` here and use `span` on the
 * `<Col>`s instead (see SPAN above).
 */
export const Row = React.forwardRef(
  ({ cols = { base: 1 }, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'tw-grid tw-gap-4',
        COLS.base[cols.base] || COLS.base[1],
        cols.sm && COLS.sm[cols.sm],
        cols.md && COLS.md[cols.md],
        cols.lg && COLS.lg[cols.lg],
        className
      )}
      {...props}
    />
  )
)
Row.displayName = 'Row'

export const Col = React.forwardRef(({ span, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw-min-w-0',
      span?.base && SPAN.base[span.base],
      span?.md && SPAN.md[span.md],
      span?.lg && SPAN.lg[span.lg],
      className
    )}
    {...props}
  />
))
Col.displayName = 'Col'
